import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Divider,
  Avatar,
  Chip,
  Card,
  CardContent,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  alpha,
  Fade,
  Zoom,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
  ContactMail as ContactMailIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AuthService from '../Auth/AuthService';
import { useNavigate } from 'react-router-dom';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  maxWidth: 1200,
  marginLeft: 'auto',
  marginRight: 'auto',
  borderRadius: theme.spacing(3),
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
  boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const HeaderAvatar = styled(Avatar)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  width: 56,
  height: 56,
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: alpha(theme.palette.background.paper, 0.6),
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const LabelTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

const ValueTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
  fontSize: '0.95rem',
  wordBreak: 'break-word',
}));

const ContactViewPage = () => {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [page, setPage] = useState(1);
  const rowsPerPage = 3;
  const hasFetched = useRef(false);
  const navigate = useNavigate();

  // Helper to get contact type labels
  const getContactTypeLabels = (types) => {
    const labelMap = {
      contactus: { label: 'Contact Us', color: 'primary', icon: <EmailIcon /> },
      feedback: { label: 'Feedback', color: 'info', icon: <CategoryIcon /> },
      suggestion: { label: 'Suggestion', color: 'warning', icon: <CategoryIcon /> },
    };
    return types.map(type => labelMap[type] || { label: type, color: 'default', icon: null });
  };

  // Format mobile number
  const formatMobile = (number) => {
    if (!number) return 'N/A';
    const str = String(number);
    if (str.length === 10) {
      return `${str.slice(0, 3)}-${str.slice(3, 6)}-${str.slice(6)}`;
    }
    return str;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchContactsRecords();
  }, []);

  const fetchContactsRecords = async () => {
    // setLoading(true);
    try {
      const rest = AuthService.pathUrl("GET", "/contacts");
      const response = await AuthService.getAuth(rest);

      if (response && response) {
        setContacts(response);
      }
    } catch (err) {
      console.error('Error fetching OTP records:', err);
      // } finally {
      //     setLoading(false);
    }
  };

  const getFilteredContacts = () => {
    return contacts.filter(contact => {
      // Filter by contact type
      if (selectedType !== 'all' && !contact.contactType.includes(selectedType)) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          contact.fullName.toLowerCase().includes(searchLower) ||
          contact.organization.toLowerCase().includes(searchLower) ||
          contact.cityState.toLowerCase().includes(searchLower) ||
          contact.description.toLowerCase().includes(searchLower) ||
          contact.contactType.some(type => type.toLowerCase().includes(searchLower))
        );
      }

      return true;
    });
  };

  const filteredContacts = getFilteredContacts();
  const totalPages = Math.ceil(filteredContacts.length / rowsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedType('all');
    setPage(1);
  };

  const handleRefresh = () => {
    fetchContactsRecords();
  }

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const dataStr = JSON.stringify(filteredContacts, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `contacts_${Date.now()}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Container maxWidth="xl">
      <Fade in timeout={600}>
        <StyledPaper elevation={0}>
          {/* Header */}
          <HeaderBox>
            <HeaderLeft>
              <HeaderAvatar>
                <ContactMailIcon sx={{ fontSize: 30 }} />
              </HeaderAvatar>
              <Box>
                <Typography variant="h4" component="h2" fontWeight={700} color="primary">
                  Contact Management
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Contacts: {filteredContacts.length}
                </Typography>
              </Box>
            </HeaderLeft>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={handlePrint} color="primary">
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download JSON">
                <IconButton onClick={handleDownload} color="primary">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </HeaderBox>

          {/* Search Section */}
          <SearchContainer elevation={0}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by name, organization, city, or description..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  variant="outlined"
                  size="medium"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchTerm('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Contact Type</InputLabel>
                  <Select
                    value={selectedType}
                    onChange={handleTypeChange}
                    label="Filter by Contact Type"
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterListIcon color="primary" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="contactus">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="primary" />
                        Contact Us
                      </Box>
                    </MenuItem>
                    <MenuItem value="feedback">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" color="info" />
                        Feedback
                      </Box>
                    </MenuItem>
                    <MenuItem value="suggestion">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" color="warning" />
                        Suggestion
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={clearSearch}
                  startIcon={<ClearIcon />}
                  sx={{ height: '56px' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </SearchContainer>

          {/* Results */}
          {filteredContacts.length === 0 ? (
            <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
              No contacts found matching your search criteria.
            </Alert>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedContacts.map((contact, index) => (
                  <Grid item xs={12} key={contact.id}>
                    <Zoom in style={{ transitionDelay: `${index * 100}ms` }}>
                      <StyledCard>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {contact.fullName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ID: #{String(contact.id).padStart(6, '0')}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {getContactTypeLabels(contact.contactType).map((type, idx) => (
                                <StyledChip
                                  key={idx}
                                  icon={type.icon}
                                  label={type.label}
                                  color={type.color}
                                  variant="filled"
                                  size="small"
                                />
                              ))}
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SchoolIcon fontSize="small" color="action" />
                                <Box>
                                  <LabelTypography>Organization</LabelTypography>
                                  <ValueTypography>{contact.organization}</ValueTypography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon fontSize="small" color="action" />
                                <Box>
                                  <LabelTypography>Mobile</LabelTypography>
                                  <ValueTypography>{formatMobile(contact.mobile)}</ValueTypography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationIcon fontSize="small" color="action" />
                                <Box>
                                  <LabelTypography>City / State</LabelTypography>
                                  <ValueTypography>{contact.cityState}</ValueTypography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon fontSize="small" color="action" />
                                <Box>
                                  <LabelTypography>Submitted</LabelTypography>
                                  <ValueTypography>{formatDate(contact.createdAt)}</ValueTypography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <DescriptionIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                <Box>
                                  <LabelTypography>Description</LabelTypography>
                                  <ValueTypography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                    {contact.description}
                                  </ValueTypography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>

                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => navigate("/contact/view/", contact.id)}
                            >
                              View Details
                            </Button>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </StyledPaper>
      </Fade>
    </Container>
  );
};

export default ContactViewPage;